import { useRef } from 'react';
import { useLoaderData } from 'react-router';

import { cn } from '@rocco/utils/cn';
import { usePageCtas } from '~/modules/enquiry/component/PageCtasProvider';
import {
    useBookCta,
    useEnquireCta,
} from '~/modules/enquiry/hooks/use-page-ctas';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    CONTENT_CAP_PADDED,
    detailCta,
    SectionHead,
} from '~/ui-rocco/pages/detail-shared';
import {
    ACTION_CARD_ACTION_CLASS,
    ACTION_CARD_CLASS,
    ACTION_CARD_CONTACT_IMAGE,
    ACTION_CARD_WEBSITE_IMAGE,
    ACTION_CARDS_GRID_CLASS,
    ActionCardBody,
    ContactPeek,
} from '~/ui-rocco/pages/detail-shared/DetailActionCards';
import { BOOK_A_MEETING } from '../hooks/use-project-enquiry-modal';
import { collectProjectImages } from './collect-project-images';
import {
    ModalPeek,
    useGrowToModal,
} from '~/ui-rocco/pages/detail-shared/ModalPeek';
import { BookMeetingMiniModal } from '~/ui-rocco/pages/detail-shared/BookMeetingPeek';
import { EnquiryMiniModal } from '~/ui-rocco/pages/detail-shared/EnquiryPeek';

const SEND_AN_ENQUIRY = 'Send an enquiry';

/**
 * The product page's two-card offer, on the project: book a meeting with the
 * practice, or send them an enquiry — each a photograph with a sentence
 * saying what the reader gets for taking it, and the whole card the control.
 *
 * Book a Meeting opens the one booking gate every entry point on the page
 * opens; with the feature off it falls back to the enquiry modal, as the
 * summary's button does. Each card drops out when its button is switched off
 * or hidden from this visitor, and the section with them when neither shows.
 */
export const ProjectHireSection = () => {
    const { project } = useLoaderData<typeof loader>();
    const professional = project.Professional;
    const { ctas } = usePageCtas();
    const bookCta = useBookCta({
        source: 'ProjectHireSection',
        fixedLabel: BOOK_A_MEETING,
    });
    const enquireCta = useEnquireCta({
        source: 'ProjectHireSection',
        fixedLabel: SEND_AN_ENQUIRY,
    });
    // The gate's miniature grows into the gate; the enquiry modal standing in
    // for it keeps the practice's card.
    const bookViaGate = ctas.book.viaGate;

    const practice = professional.Title || 'the professional';
    const role = professional.Categories?.[0]?.PrettyTitle ?? '';
    const banner = project.Banner?.imageUrl || project.FacebookPreviewImage;

    // The project's own photographs ground the two cards, skipping the
    // banner the page opens on and the peek card repeats. The practice's
    // profile banners top the pool up where a project has few, and the
    // bundled frames catch a project with none.
    const gallery = collectProjectImages(project.Modules, project.Title)
        .map((slide) => slide.src)
        .filter((src) => src !== banner);
    const practiceBanners = (professional.BannerImages ?? [])
        .map((image) => image?.LibraryImageFile?.DetailBannerStandard?.Desktop)
        .filter((src): src is string => !!src);
    const cardImagePool = [...gallery, ...practiceBanners];
    const meetingCardImage = cardImagePool[0] ?? ACTION_CARD_WEBSITE_IMAGE;
    const enquiryCardImage = cardImagePool[1] ?? ACTION_CARD_CONTACT_IMAGE;
    // The practice's own banner on the meeting card's peek, not their logo: a
    // mark on a white square reads as a badge, a photograph as their work.
    const practicePeekImage = practiceBanners[0] ?? banner ?? undefined;

    // Each card's hover is the modal it opens, small, and the click grows it
    // into the real one. With the booking gate off the meeting card falls
    // back to the enquiry modal, as the summary's button does, and its hover
    // stays the practice's card.
    const meetingPeekRef = useRef<HTMLDivElement>(null);
    const meetingMini = (
        <BookMeetingMiniModal
            subject={{
                image: practicePeekImage,
                practice,
                companyLogo: professional.CompanyLogo,
                logoBackgroundColor: professional.LogoBackgroundColor,
            }}
        />
    );
    const meetingFlight = useGrowToModal(meetingPeekRef, meetingMini);
    const onBookMeeting = bookViaGate
        ? () => meetingFlight.grow(bookCta.open)
        : bookCta.open;

    // The enquiry gate is the one every entry point opens, so its miniature
    // needs no fallback.
    const enquiryPeekRef = useRef<HTMLDivElement>(null);
    const enquiryMini = (
        <EnquiryMiniModal
            subject={{
                image: banner ?? undefined,
                practice,
                note: professional.CustomEnquiryMessage,
                itemType: 'project',
            }}
        />
    );
    const enquiryFlight = useGrowToModal(enquiryPeekRef, enquiryMini);
    const onSendEnquiry = () => enquiryFlight.grow(() => enquireCta.open());

    if (!bookCta.visible && !enquireCta.visible) return null;

    return (
        <section className="w-full bg-transparent max-md:px-6 md:px-[3.125rem]">
            <div className={CONTENT_CAP_PADDED}>
                {/* The tail's own head, as Professionals used sets it: the
                    question on one line, the practice on the next, which
                    is also the way to their profile. */}
                <SectionHead
                    tone="dark"
                    lead="Like this Project?"
                    subject={`Hire ${practice}`}
                    subjectLink={professional.Link}
                />

                <div className={ACTION_CARDS_GRID_CLASS}>
                    {bookCta.visible ? (
                        <button
                            type="button"
                            onClick={onBookMeeting}
                            className={ACTION_CARD_CLASS}
                        >
                            <ActionCardBody
                                image={meetingCardImage}
                                hoverTint
                                peek={
                                    bookViaGate ? (
                                        <ModalPeek boxRef={meetingPeekRef}>
                                            {meetingMini}
                                        </ModalPeek>
                                    ) : (
                                        <ContactPeek
                                            image={practicePeekImage}
                                            title={practice}
                                            subtitle={role}
                                        />
                                    )
                                }
                                copy={
                                    <>
                                        Book a meeting with {practice} to talk
                                        through your own project, their approach
                                        and how they could take it on.
                                    </>
                                }
                                action={
                                    <span
                                        className={cn(
                                            detailCta('white-lifted'),
                                            ACTION_CARD_ACTION_CLASS,
                                        )}
                                    >
                                        {bookCta.label}
                                    </span>
                                }
                            />
                        </button>
                    ) : null}

                    {enquireCta.visible ? (
                        <button
                            type="button"
                            onClick={onSendEnquiry}
                            className={ACTION_CARD_CLASS}
                        >
                            <ActionCardBody
                                image={enquiryCardImage}
                                hoverTint
                                peek={
                                    <ModalPeek boxRef={enquiryPeekRef}>
                                        {enquiryMini}
                                    </ModalPeek>
                                }
                                copy={
                                    <>
                                        Send {practice} an enquiry about your
                                        project, their availability and fees, or
                                        anything you would like to know first.
                                    </>
                                }
                                action={
                                    <span
                                        className={cn(
                                            detailCta('white-lifted'),
                                            ACTION_CARD_ACTION_CLASS,
                                        )}
                                    >
                                        {enquireCta.label}
                                    </span>
                                }
                            />
                        </button>
                    ) : null}
                </div>
            </div>
            {meetingFlight.element}
            {enquiryFlight.element}
        </section>
    );
};
